import { notify } from '@billy/design-system'
import { useCallback, useState } from 'react'
import { FileRejection } from 'react-dropzone'
import { useTranslation } from 'react-i18next'

import { updateAttachment, uploadFile } from '@features/attachments/query-api'
import { useInvalidateTransactions } from '@features/transactions/hooks/useInvalidateTransactions'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { NotificationKeys } from '../../../enums/notificationKeys'
import { TrackingContext } from '../../../enums/trackingContext'
import { useSegment } from '../../../hooks'
import { useInvalidateDaybookTransaction } from './useInvalidateDaybookTransaction'

interface UseUploadDaybookTransactionAttachmentsParams {
  daybookTransactionId?: string
  onUploadStart?: () => void
  onUploadFinished?: () => void
}

export const useUploadDaybookTransactionAttachments = ({
  daybookTransactionId,
  onUploadStart,
  onUploadFinished,
}: UseUploadDaybookTransactionAttachmentsParams) => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { organization } = useUserOrganization()
  const { invalidateDaybookTransaction } = useInvalidateDaybookTransaction()
  const { invalidateTransactions } = useInvalidateTransactions()
  const [isUploading, setIsUploading] = useState(false)

  const handleFilesDropped = useCallback(
    async (filesAccepted: File[], _filesRejected?: FileRejection[]) => {
      if (!organization?.id || !daybookTransactionId) {
        return
      }

      setIsUploading(true)
      onUploadStart?.()

      try {
        for (const file of filesAccepted) {
          const response = await uploadFile(file, {
            'x-organizationId': organization.id,
            'x-should-scan': true,
            'x-create-attachment': true,
          })

          const attachmentId = response.attachments?.[0]?.id

          if (attachmentId) {
            await updateAttachment({
              id: attachmentId,
              ownerReference: `daybookTransaction:${daybookTransactionId}`,
              ownerId: daybookTransactionId,
              organizationId: organization.id,
            })
          }
        }

        track('File Uploaded (Client)', { context: TrackingContext.DaybookAttachment })
        invalidateDaybookTransaction(daybookTransactionId)
        invalidateTransactions()
        notify({
          id: NotificationKeys.DaybookTransactionAttachmentFilesUpdate,
          message: t('journal.notification.attachment_upload_success'),
          variant: 'success',
        })
      } catch {
        notify({
          id: NotificationKeys.UploadFiles,
          message: t('file.rejected'),
          variant: 'error',
        })
      } finally {
        setIsUploading(false)
        onUploadFinished?.()
      }
    },
    [
      daybookTransactionId,
      organization,
      t,
      track,
      invalidateDaybookTransaction,
      invalidateTransactions,
      onUploadStart,
      onUploadFinished,
    ],
  )

  return { handleFilesDropped, isUploading }
}
